import { useState } from "react";
import { RefreshCw, Send } from "lucide-react";
import { toast } from "sonner";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  BrandHubOrderInput,
  BrandHubOrderSent,
  BrandHubStatus,
} from "@shared-core/data/journey-testing-types";
import { Button } from "../../app/components/primitives/button";
import { Input } from "../../app/components/primitives/input";
import { Label } from "../../app/components/primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../app/components/primitives/select";
import { EmptyRow, describeError } from "./consoleState";

const ENVELOPES: Array<{
  value: NonNullable<BrandHubOrderInput["envelope"]>;
  label: string;
}> = [
  { value: "account_mixed_lines", label: "Account, mixed lines" },
  { value: "express_card_vat5", label: "Express, card, 5% VAT" },
  { value: "card_no_delivery_vat0", label: "Card, no delivery, 0% VAT" },
];
const MODES: Array<NonNullable<BrandHubOrderInput["mode"]>> = [
  "shadow",
  "live",
  "dry_run",
];

/** BrandHub (07 §1): play BrandHub — send a saved envelope to the real intake, then poll its status. */
export function BrandHubTab() {
  const adapter = useDataAdapter();
  const [envelope, setEnvelope] = useState<
    NonNullable<BrandHubOrderInput["envelope"]>
  >("account_mixed_lines");
  const [mode, setMode] =
    useState<NonNullable<BrandHubOrderInput["mode"]>>("shadow");
  const [customerCode, setCustomerCode] = useState("");
  const [quantity, setQuantity] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<BrandHubOrderSent[]>([]);
  const [statuses, setStatuses] = useState<BrandHubStatus | null>(null);

  async function send() {
    setSending(true);
    try {
      const result = await adapter.sendBrandHubOrder({
        envelope,
        mode,
        customer_code: customerCode.trim() || null,
        quantity: quantity ? Number(quantity) : null,
      });
      setSent((rows) => [result, ...rows]);
      toast.success(
        `BrandHub order ${result.brandhub_order_id}: the intake answered ${result.status_code}.`,
      );
    } catch (err) {
      toast.error(describeError(err));
    } finally {
      setSending(false);
    }
  }

  async function refresh() {
    try {
      setStatuses(
        await adapter.getBrandHubStatus(sent.map((s) => s.brandhub_order_id)),
      );
    } catch (err) {
      toast.error(describeError(err));
    }
  }

  return (
    <div className="space-y-6">
      <section className="grid gap-4 rounded-lg border p-4 md:grid-cols-4 md:items-end">
        <div className="grid gap-1.5">
          <Label htmlFor="brandhub-envelope">Envelope</Label>
          <Select
            value={envelope}
            onValueChange={(v) => setEnvelope(v as typeof envelope)}
          >
            <SelectTrigger id="brandhub-envelope">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ENVELOPES.map((e) => (
                <SelectItem key={e.value} value={e.value}>
                  {e.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="brandhub-mode">Mode</Label>
          <Select value={mode} onValueChange={(v) => setMode(v as typeof mode)}>
            <SelectTrigger id="brandhub-mode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MODES.map((m) => (
                <SelectItem key={m} value={m}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="brandhub-customer">
            Customer code (envelope's when empty)
          </Label>
          <Input
            id="brandhub-customer"
            value={customerCode}
            onChange={(e) => setCustomerCode(e.target.value)}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="brandhub-quantity">
            Quantity (envelope's when empty)
          </Label>
          <Input
            id="brandhub-quantity"
            inputMode="numeric"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value.trim())}
          />
        </div>
        <div className="md:col-span-4">
          <Button onClick={() => void send()} disabled={sending}>
            <Send className="size-4" />
            Send to the intake
          </Button>
        </div>
      </section>
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Sent this session</h2>
          <Button
            size="sm"
            variant="outline"
            disabled={sent.length === 0}
            onClick={() => void refresh()}
          >
            <RefreshCw className="size-4" />
            Refresh status
          </Button>
        </div>
        {sent.length === 0 ? (
          <EmptyRow>No envelopes sent yet.</EmptyRow>
        ) : (
          <ul className="space-y-2 text-sm">
            {sent.map((row) => (
              <li key={row.brandhub_order_id} className="rounded-md border p-3">
                <div className="font-mono">
                  #{row.brandhub_order_id} · intake {row.status_code}
                </div>
                <pre className="mt-1 overflow-x-auto text-xs text-muted-foreground">
                  {JSON.stringify(row.response, null, 2)}
                </pre>
              </li>
            ))}
          </ul>
        )}
        {statuses && (
          <pre
            className="overflow-x-auto rounded-md bg-muted p-3 text-xs"
            aria-label="Status poll"
          >
            {JSON.stringify(statuses.response, null, 2)}
          </pre>
        )}
      </section>
    </div>
  );
}
